<template>
  <div id="ubicationWrapper">
    <div class="my-3">
      <ValidationObserver ref="observer" novalidate slim>
        <Section1
          :key="componentKey"
          :municipalities-list="municipalities"
          :model="model"
          from-management
          :is-expanded="isExpandedSection1"
          :is-read-only="isSubmitted"
          :show-msg-error-section1="showMsgErrorSection1"
          @expanded="onExpanded"
          @resetMsgErrorSection1="resetMsgErrorSection1"
        />
      </ValidationObserver>
    </div>

    <template v-if="model.id">
      <ValidationObserver ref="main-form">
        <div class="my-3">
          <Section2
            :model="model"
            :is-read-only="isSubmitted"
            :tech-assistance-questions="techAssistanceQuestions"
          />
        </div>

        <div class="my-3">
          <ValidationProvider
            name="Servicios provistos"
            v-slot="{ validate, errors }"
            :rules="{ required: { allowFalse: false } }"
          >
            <ServicesProvided
              :tap-request-id="model.id"
              :submitted="readonly"
              @input="() => validate && validate()"
            />
            <Error
              v-if="errors[0]"
              variant="no-border"
              message="Favor de añadir servicios provistos."
            />
          </ValidationProvider>
        </div>

        <div class="my-3">
          <UbicationAlternative
            :model="model.alternativeUbication"
            :is-read-only="isSubmitted"
          />
        </div>

        <div class="my-3">
          <ValidationProvider
            name="Propósito de la consulta"
            v-slot="{ validate, errors }"
            :rules="{ required: { allowFalse: false } }"
          >
            <Purposes
              :purposes="model.purposes"
              :submitted="!!isSubmitted"
              @input="() => validate && validate()"
            />
            <Error
              v-if="errors[0]"
              variant="no-border"
              message="Favor marcar el propósito de la consulta."
            />
          </ValidationProvider>
        </div>

        <div
          v-if="showServicesSummary && servicesSummary.length > 0"
          class="my-3"
        >
          <ServicesSummary
            :services="servicesSummary"
            :collections="servicesSummaryCollections"
            :readonly="true"
            :hide-total="false"
          />
        </div>
        <div v-else-if="showServicesSummary" class="my-3">
          <b-alert show variant="info" class="mb-0">Sin servicios</b-alert>
        </div>

        <div class="my-3">
          <ValidationProvider
            name="Documentos"
            v-slot="{ errors }"
            vid="document"
            :custom-messages="{ required: 'Favor anejar un documento.' }"
          >
            <Documents
              :key="documentLength"
              :documents="document.data"
              :document-types="document.documentTypes"
              :disabled="isSubmitted"
              @files="gettingDocument($event)"
            />
            <Error v-if="errors[0]" variant="no-border" :message="errors[0]" />
          </ValidationProvider>
        </div>

        <div class="my-3">
          <Requesters
            :allow-add-more="false"
            :submitted="isSubmitted"
            :requesters="model.requesters"
            :positions-list="requestersPositionsList"
            :value="model.requesters.length"
            @input="() => validate && validate()"
          />
        </div>

        <div class="my-3">
          <div class="row">
            <div class="col-12">
              <div class="tag-badge pl-2">
                <span class="dot text-center">
                  <i
                    class="far fa-check-circle orange-icon orange-icon-badge-align"
                  />
                </span>
                <span class="tag-badge-title py-0 pl-3">
                  Términos y Responsabilidades
                </span>
              </div>
              <div class="row mx-1 pt-2">
                <div class="col-12 px-0">
                  <div class="border-box">
                    Certifico por este medio, que esta solicitud se encontrará
                    en el expediente físico del estudiante firmado por todas las
                    partes involucradas. (0502)
                  </div>
                </div>
              </div>
              <ValidationProvider
                v-slot="{ errors }"
                name="checkboxConditions"
                :rules="{ required: { allowFalse: false } }"
              >
                <div class="row mx-1">
                  <div
                    class="border-box"
                    :class="{
                      error: errors.length > 0,
                      signed: termsSelected,
                      unsigned: !termsSelected,
                    }"
                  >
                    <div class="col-12 px-0 py-0 h-100 ">
                      <div class="custom-control custom-checkbox h-100 ">
                        <div
                          class="custom-control custom-checkbox h-100 d-flex justify-content-start align-items-center "
                        >
                          <input
                            id="checkboxConditions"
                            v-model="termsSelected"
                            :disabled="isSubmitted"
                            type="checkbox"
                            class="custom-control-input"
                            name="checkboxConditions"
                          />
                          <label
                            class="custom-control-label pl-md-3 "
                            for="checkboxConditions"
                          >
                            Acepto los términos y responsabilidades
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <Error
                  v-if="errors[0]"
                  variant="no-border"
                  message="Favor certificar los términos y responsabilidades."
                />
              </ValidationProvider>
            </div>
          </div>
        </div>
      </ValidationObserver>
    </template>
  </div>
</template>

<script>
import { Section1, Section2 } from './_components';
import {
  ServicesProvided,
  UbicationAlternative,
  Purposes,
  Documents,
  Requesters,
} from '@/components/pei/ubication-tech-assistance/_components';
import { Error } from '@/components/common';
import alert from '@/mixin/alert.mixin';
import {
  getMunicipalities,
  getPositions,
  getPurposes,
  getDocumentTypesByCode,
} from '@/services/api/tapRequest.api.js';
import { UTA_MANAGEMENT_SECTION_ENUM } from '@/components/pei/ubication-tech-assistance/_config/management';
import ServicesSummary from '@/components/pei/ubication-tech-assistance/management/_components/CentralLevelAnalysis/components/ServicesSummary/Main.vue';

const QuestionsEnum = { Purposes: 36 };
const TapRequestStatus = { New: 1, Submitted: 2 };

const modelInitialState = () => ({
  id: null,
  municipality: {},
  municipalityId: 0,
  facilitatorName: null,
  facilitatorDiscussionOccurred: false,
  facilitatorDiscussionDescription: null,
  facilitatorDiscussionFile: null,
  studentEducationalFunctioningDesc: null,
  alternativeUbication: {
    ageRange: null,
    alternative: null,
    justification: null,
    description: null,
    focusTypeId: null,
    gradeId: null,
  },
  sectionsQuestions: [],
  arrangements: [],
  requesters: [],
  purposes: [],
});

export default {
  name: 'UbicationTechAssistanceWrapper',
  mixins: [alert],
  props: {
    readonly: { type: Boolean, default: false },
    modelData: { type: Object, default: null },
    sectionsStates: { type: Object, default: () => ({}) },
    showServicesSummary: { type: Boolean, default: false },
    servicesSummary: { type: Array, default: () => [] },
    servicesSummaryCollections: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      documentLength: 0,
      enums: { sections: UTA_MANAGEMENT_SECTION_ENUM },
      componentKey: 0,
      isExpandedSection1: true,
      showMsgErrorSection1: false,
      showMsgErrorAlternatives: false,
      //
      model: modelInitialState(this.studentSieId),
      requestersPositionsList: [],
      municipalities: [],
      // Data for component Document.
      document: {
        documentTypes: [],
        // Variables to save component data.
        data: {
          files: [],
          types: [],
        },
      },
      page: {
        icon: 'fas fa-user-edit',
        title: 'Solicitud de Asistencia Técnica para Ubicación del Estudiante',
        routes: [
          'PEI',
          'Solicitud de Asistencia Técnica para Ubicación del Estudiante',
        ],
      },
      hasServicesAdded: false,
      termsRequired: true,
      termsSelected: false,
      isSubmitted: false,
      techAssistanceQuestions: [],
    };
  },
  components: {
    Section1,
    Section2,
    ServicesProvided,
    UbicationAlternative,
    Requesters,
    Documents,
    Purposes,
    Error,
    ServicesSummary,
  },
  methods: {
    async getMunicipalities() {
      const { data } = await getMunicipalities();
      this.municipalities = data;
    },
    async getPurposes() {
      const { data } = await getPurposes(13);
      this.techAssistanceQuestions = data;
      this.model.purposes = data
        .filter((x) => x.id === QuestionsEnum.Purposes)[0]
        .questions.map((x) => {
          x.selected = false;
          return x;
        });
    },
    async getPositions() {
      const { data } = await getPositions();
      this.requestersPositionsList = data;
    },
    groupBy(keys) {
      return (array) => {
        return array.reduce((objectsByKeyValue, obj) => {
          const value = keys.map((key) => obj[key]).join('-');
          objectsByKeyValue[value] = (objectsByKeyValue[value] || []).concat(
            obj
          );
          return objectsByKeyValue;
        }, []);
      };
    },
    prepareAreaArrangements(arrangements) {
      const groupByArea = this.groupBy(['arrangementAreaId']);
      return groupByArea(arrangements)
        .map((item) => ({
          area: {
            name: item[0].arrangementArea,
            id: item[0].arrangementAreaId,
          },
          selected: item.map((a) => ({
            areaId: a.arrangementAreaId,
            id: a.arrangementId,
            name: a.arrangement,
          })),
        }))
        .filter((r) => r !== undefined);
    },
    async fillModelData() {
      if (this.modelData) {
        const {
          id,
          facilitatorDiscussionOccurred,
          facilitatorDiscussionDescription,
          facilitatorDiscussionFile,
          studentEducationalFunctioningDesc,
          recommendedPlacement,
          recommendedPlacementId,
          recommendedPlacementGradeId,
          recommendedPlacementJustification,
          recommendedPlacementFocusTypeId,
          recommendedPlacementDescription,
          questionnaireAnswers,
          arrangements,
          staffMembers,
          documents,
          facilitatorName,
          municipalityId,
          statusId,
        } = this.modelData;

        this.isSubmitted =
          statusId === TapRequestStatus.Submitted || this.readonly;
        this.termsSelected = this.isSubmitted;
        this.model.facilitatorName = facilitatorName;
        this.model.municipality = { id: municipalityId };

        this.model.id = id;
        this.model.documents = documents;
        facilitatorDiscussionOccurred &&
          (this.model.facilitatorDiscussionOccurred = facilitatorDiscussionOccurred);
        facilitatorDiscussionDescription &&
          (this.model.facilitatorDiscussionDescription = facilitatorDiscussionDescription);
        facilitatorDiscussionFile &&
          (this.model.facilitatorDiscussionFile = facilitatorDiscussionFile);
        studentEducationalFunctioningDesc &&
          (this.model.studentEducationalFunctioningDesc = studentEducationalFunctioningDesc);
        this.model.alternativeUbication = {
          ageRange: null,
          alternative: recommendedPlacementId
            ? { id: recommendedPlacementId, text: recommendedPlacement }
            : null,
          justification: recommendedPlacementJustification,
          description: recommendedPlacementDescription,
          focusType: recommendedPlacementFocusTypeId
            ? { id: recommendedPlacementFocusTypeId, text: '' }
            : null,
          gradeId: recommendedPlacementGradeId,
        };
        questionnaireAnswers &&
          (this.model.sectionsQuestions = questionnaireAnswers);

        arrangements &&
          (this.model.arrangements = this.prepareAreaArrangements(
            arrangements
          ));
        staffMembers &&
          (this.model.requesters =
            staffMembers?.map(
              ({
                fullName,
                workPosition,
                workPositionId,
                telephoneNumber,
                email,
                signed,
                signDate,
              }) => ({
                name: fullName,
                position: {
                  id: workPositionId || null,
                  name: workPosition || null,
                },
                phone: telephoneNumber,
                email,
                signed: this.isSubmitted || signed,
                signDate,
              })
            ) || []);
        questionnaireAnswers &&
          (this.model.purposes = this.model.purposes.map(
            ({ id, text, answers: [first] }) => ({
              questionId: id,
              answerId: first?.id || null,
              text,
              description:
                questionnaireAnswers.find(
                  (c) => c.questionId === id && c.questionId === 226
                )?.otherAnswerText || null,
              selected:
                questionnaireAnswers?.some((c) => c.questionId === id) || false,
            })
          ));
        this.fillDocuments();
        this.componentKey++;
      }
    },
    resetMsgErrorSection1() {
      this.showMsgErrorSection1 = false;
    },
    onExpanded(value) {
      this.isExpandedSection1 = value;
    },
    async getDocumentTypesByCode(code) {
      const { data } = await getDocumentTypesByCode(code);
      if (data) {
        data.forEach((element) => {
          this.document.documentTypes.push({
            id: element.id,
            name: element.name,
          });
        });
      }
    },
    // Getting data from BackupDocument component.
    async gettingDocument(data) {
      this.document.data = data;
    },
    // Filling component documents.
    fillDocuments() {
      this.document.data = {
        files: [],
        types: [],
      };
      if (this.model.documents && this.model.documents.length > 0) {
        this.model.documents
          .filter((x) => x.document.documentTypeId != 175)
          .forEach((element) => {
            this.document.data.files.push({
              name: element.filename,
              documentType: element.document.documentTypeId,
              documentTypeName: element.document.documentTypeName,
              id: element.documentId,
              readOnly: element.readOnly,
              inServer: true,
              createdDate: element.createdDate,
              userName: element.userName,
              userFullName: element.userFullName,
              isAmendmentDocument: element.isAmendmentDocument,
            });
            this.document.data.types.push(element.document.documentTypeId);
          });
      }
      this.documentLength++;
    },
    validateDocuments() {
      const hasDocuments =
        this.document.data != undefined && this.document.data.files.length > 0;
      if (!hasDocuments) {
        const noDocError = 'Favor anejar un documento.';
        this.$refs['main-form'].setErrors({ document: [noDocError] });
      }

      const hasSAEE08File = this.document.data.types.includes(177);
      if (!hasSAEE08File) {
        const errorMsg =
          'Para someter la Solicitud, es requerido anejar el documento de SAEE-08 - Minuta de Reunión.';
        this.$refs['main-form'].setErrors({ document: [errorMsg] });
        this.ShowToast('', errorMsg, 'error');
      }
      return hasDocuments && hasSAEE08File;
    },
    goBack() {
      this.$router.push({ path: '/pei' });
    },
  },
  watch: {
    'model.municipality'(newValue) {
      this.model.municipalityId = newValue.id;
    },
    deep: true,
    immediate: true,
  },
  async created() {
    Promise.all([this.getMunicipalities(), this.getPositions()]);
    await this.getPurposes();
    await this.getDocumentTypesByCode('TapRequest');
    await this.fillModelData();
  },
};
</script>

<style scoped>
.disabled-page {
  pointer-events: none;
}

.tag-badge {
  display: inline-block;
  padding-right: 1em;
  height: 42px;
  background: transparent linear-gradient(90deg, #ffffff 0%, #e8e8e8 100%) 0% 0%
    no-repeat padding-box;
  border-radius: 26px;
  cursor: pointer;
  margin-bottom: 10px;
}

.dot {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  display: inline-block;
  background: #ffffff 0% 0% no-repeat padding-box;
  box-shadow: 0px 3px 6px #00000029;
  padding-top: 9px;
}
.orange-icon {
  color: #ff7058;
}

.orange-icon-badge-align {
  vertical-align: top;
}

.tag-badge-title {
  text-align: left;
  font: Bold 25px/30px Lato;
  letter-spacing: 0;
  color: #11291b;
  display: inline-block;
}
@media screen and (max-width: 765px) {
  .tag-badge-title {
    font-size: 18px;
  }
}
.border-box {
  border: 1px solid #e2e2e2;
  border-radius: 5px 5px 0px 0px;
  padding: 20px;
  width: 100%;
}
.border-box.signed,
.border-box.unsigned {
  border-radius: 0px 0px 5px 5px;
  border-top: none;
  padding: 0 !important;
  height: 65px;
}
.border-box.error {
  background: #ffebeb 0% 0% no-repeat padding-box;
  border: 1px solid #c9545e;
}

.signed {
  background-color: #2db782;
  color: white;
}
.unsigned {
  background-color: #f7f7f7;
  color: black;
}
.question {
  box-shadow: -3px 1px 3px #00000029;
  align-content: space-between;
}

.item-pane {
  justify-content: space-between;
  min-width: 50%;
  box-shadow: -3px 1px 3px #00000029;
  margin-top: 10px;
  display: flex;
  border-bottom: solid 1px #e2e2e2;
}

.question-box {
  width: 65px;
  background: #ff7058 0% 0% no-repeat padding-box;
  border-radius: 5px 5px 0px 0px;
  text-align: center;
  color: #ffffff;
  letter-spacing: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.box-number {
  font: Bold 32px/19px Lato;
}
.question-line.pl-55 {
  padding-left: 55px !important;
}
.question-line p {
  padding-bottom: 0px;
}

.question-line p >>> p {
  margin-bottom: 0px !important;
}

.header {
  font: Bold 16px/22px Lato;
  letter-spacing: 0;
  color: #ffffff;
  opacity: 1;
  background: #3f5b60 0% 0% no-repeat padding-box;
  border-radius: 5px 5px 0px 0px;
  opacity: 1;
}

.row-checkbox {
  width: 20px;
  height: 20px;
  width: 10%;
  background: #ffffff 0% 0% no-repeat padding-box;
  border: 1px solid #e2e2e2;
  border-radius: 4px;
  opacity: 1;
}

.row-text {
  width: 90%;
}

.row-odd {
  background-color: #fafbfa;
}

.row-even {
  background-color: #ffffff;
}

.text-fix {
  font-weight: 400;
}
.relative-top-10 {
  position: relative;
  top: 0.8em;
}
#ubicationWrapper >>> .tag-badge-title-info {
  font-size: 15px;
}
.blured-body {
  left: 119px;
  top: 100px;
  position: fixed;
  height: 100%;
  width: 100%;
  z-index: 11;
}
.blured-body {
  backdrop-filter: blur(8px);
}
.custom-control-input:disabled ~ .custom-control-label,
.custom-control-input[disabled] ~ .custom-control-label {
  color: white !important;
}
</style>
